// src/pages/ApplicationBaladaApp/components/BaladaAppFilterBox/index.tsx
import { Box, Button, Flex, Heading, Input, InputGroup, SimpleGrid, Text } from '@chakra-ui/react';
import { UseFormReturn, useController } from 'react-hook-form';
import { useDebouncedFieldRegistry } from '@/utils/filterUtils/useDebouncedFieldRegistry';
import { ChakraFeatherIcon } from 'src/components/ChakraFeatherIcon';
import { SelectField } from 'src/components/SelectField';
import { formatDateToDateInputValue } from '@/utils/formatDateToDateInputValue';
import { InputField } from '@/components/InputField';
import { BaladaAppFilters } from '../../types';
import { BaladaAppType } from '@/model';
import { useTranslation } from 'react-i18next';

export interface BaladaAppFilterBoxProps {
  filtersForm: UseFormReturn<BaladaAppFilters>;
  forceRefetch: () => void;
}

export const BaladaAppFilterBox = ({ filtersForm }: BaladaAppFilterBoxProps) => {
  const { t } = useTranslation();
  const { register, reset, control } = filtersForm;

  const startDateControl = useController({
    name: 'start_date',
    control,
  });
  const endDateControl = useController({
    name: 'end_date',
    control,
  });

  return (
    <Box>
      <Flex align='center' gap='pico' mb='deka'>
        <ChakraFeatherIcon color='text.main' name='filter' />
        <Heading variant='sectionTitle'>
          {t('pages.application_balada_app.components.balada_app_filter_box.balada_app_filter_filter_heading')}
        </Heading>
      </Flex>

      <Flex direction={{ base: 'row' }} gap='small' rowGap='micro'>
        <SimpleGrid
          gap='deka'
          alignItems='flex-end'
          columns={{ base: 1, sm: 2, md: 4, lg: 4, xl: 4 }}
          rowGap='xxsmall'
          flexGrow={1}
        >
          <InputGroup display='flex' flexDirection='column'>
            <Text fontWeight='medium'>
              {t('pages.application_balada_app.components.balada_app_filter_box.balada_app_filter_title_label')}
            </Text>
            <Input variant='flushed' {...useDebouncedFieldRegistry(() => register('title'))} />
          </InputGroup>

          <InputGroup display='flex' flexDirection='column'>
            <Text fontWeight='medium'>
              {t('pages.application_balada_app.components.balada_app_filter_box.balada_app_filter_type_label')}
            </Text>
            <SelectField {...register('type')}>
              <option value=''>
                {t('pages.application_balada_app.components.balada_app_filter_box.balada_app_filter_all_option')}
              </option>
              <option value={BaladaAppType.OTHERS}>
                {t('pages.application_balada_app.components.balada_app_filter_box.balada_app_filter_type_others')}
              </option>
              <option value={BaladaAppType.TICKETS}>
                {t('pages.application_balada_app.components.balada_app_filter_box.balada_app_filter_type_tickets')}
              </option>
              <option value={BaladaAppType.MEMBERSHIPS}>
                {t('pages.application_balada_app.components.balada_app_filter_box.balada_app_filter_type_memberships')}
              </option>
            </SelectField>
          </InputGroup>

          <InputGroup display='flex' flexDirection='column'>
            <Text fontWeight='medium'>
              {t('pages.application_balada_app.components.balada_app_filter_box.balada_app_filter_start_date_label')}
            </Text>
            <InputField
              type='date'
              fullDatePickerArea
              variant='flushed'
              value={formatDateToDateInputValue(startDateControl.field.value)}
              onChange={event => startDateControl.field.onChange(event.target.value)}
            />
          </InputGroup>

          <InputGroup display='flex' flexDirection='column'>
            <Text fontWeight='medium'>
              {t('pages.application_balada_app.components.balada_app_filter_box.balada_app_filter_end_date_label')}
            </Text>
            <InputField
              type='date'
              fullDatePickerArea
              variant='flushed'
              value={formatDateToDateInputValue(endDateControl.field.value)}
              onChange={event => endDateControl.field.onChange(event.target.value)}
              min={formatDateToDateInputValue(startDateControl.field.value)}
            />
          </InputGroup>
        </SimpleGrid>

        <Button alignSelf={{ lg: 'end' }} colorScheme='text' variant='outline' mt='10px' onClick={() => reset()}>
          {t('pages.application_balada_app.components.balada_app_filter_box.balada_app_filter_clear_button')}
        </Button>
      </Flex>
    </Box>
  );
};
